import { useState, useEffect, useRef, useCallback } from "react";
import Button from "../../Components/Button";
import { QrCodeIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { CHECKIN, SEATING_CHART } from "../../Router/path";
import { SearchNormal1 } from "iconsax-react";
import CheckInSlider from "../../Components/CheckIn/CheckInSlider";
import LoadingInComponent from "../../Components/LoadingInComponent";
import { useToast } from "../../Components/Toast";
import axios from "axios";
import {
  DIETARY_OPTIONS_API,
  GET_EVENT_DETAILS_API,
  GET_GUEST_OPTIONS_API,
  GET_GUESTS_API,
  GET_OPEN_TABLES_API,
  WALK_IN_API,
} from "../../Constant/api";
import CheckInForm from "../../Components/CheckIn/CheckInForm";
import ImageSlider from "../../Components/ImageSlider";
import { useZxing } from "react-zxing";
import { getContrastTextColor, normalizeColor } from "../../Constant/colors";
import { loadFontIfExists } from "../../Constant/fontLoader";
import { days } from "../../Constant/time";

const EventCheckInPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [guestId, setGuestId] = useState("");
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [guestOptions, setGuestOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [formData, setFormData] = useState({
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    contact: "",
    special_dietary_id: [],
    group_name: "",
    isReplacement: false,
    table_id: "",
    guest_id: "",
    note: "",
  });
  const [qrCodeReaderOpen, setQrCodeReaderOpen] = useState(false);
  const inputRef = useRef(null);

  const { toast } = useToast();
  const navigate = useNavigate();

  const { ref: videoRef } = useZxing({
    paused: !qrCodeReaderOpen,
    constraints: {
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    },
    onDecodeResult(result) {
      setGuestId(result.getText());
      setQrCodeReaderOpen(false);
      setCheckInOpen(true);
    },
    onError(error) {
      console.error("Camera error:", error);
      toast("Camera error: " + error.message, "error");
    },
  });

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropDownChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, isReplacement: checked });
  };

  const toggleModal = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  const fetchGuestList = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        axios
          .get(GET_GUESTS_API, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
            params: { eventId: sessionStorage.getItem("event_id") },
          })
          .then((response) => {
            setGuestList(response.data.data);
            if (!silent) {
              setLoading(false);
            }
          })
          .catch((error) => {
            toast("Failed to fetch guest list", "error");
            console.error(error);
            if (!silent) {
              setLoading(false);
            }
          });
      } catch (error) {
        toast("Failed to fetch guest list", "error");
        console.error(error);
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [toast]
  );

  const handleCheckIn = useCallback(
    async ({ redirect }) => {
      axios
        .post(WALK_IN_API, formData, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then(() => {
          setFormData({
            salutation: "",
            name: "",
            email: "",
            contact: "",
            special_dietary_id: "",
            group_name: "",
            isReplacement: false,
            table_id: "",
            guest_id: "",
            note: "",
          });
          fetchGuestList();
          toggleModal();
          setLoading(false);
          if (redirect) {
            navigate(SEATING_CHART);
          }
          toast("Guest checked in successfully", "success");
        })
        .catch((error) => {
          toast("Failed to check in guest", "error");
          console.error(error);
          setLoading(false);
        });
    },
    [formData, fetchGuestList, navigate, toast, toggleModal]
  );

  const validateCheckIn = useCallback(
    ({ redirect } = {}) => {
      setLoading(true);
      if (formData.table_id === "" && !redirect && !formData.isReplacement) {
        setLoading(false);
        toast("Please select a table", "error");
      } else if (
        formData.table_id === "" &&
        redirect &&
        !formData.isReplacement
      ) {
        handleCheckIn({ redirect: true });
      } else {
        handleCheckIn({ redirect: false });
      }
    },
    [formData.table_id, formData.isReplacement, handleCheckIn, toast]
  );

  const fetchEventDetails = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GET_EVENT_DETAILS_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((res) => {
          const data = res.data.data;
          setEventDetails(data);
          loadFontIfExists(data.customizations?.title_font);
          loadFontIfExists(data.customizations?.content_font);
          setLoading(false);
        })
        .catch((error) => {
          toast("Failed to fetch event details", "error");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      toast("Failed to fetch event details", "error");
      console.error(error);
      setLoading(false);
    }
  }, [toast]);

  const fetchTableOptions = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GET_OPEN_TABLES_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          setTableOptions(response.data.data);
          setLoading(false);
        })
        .catch((error) => {
          toast("Failed to fetch table list", "error");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      toast("Failed to fetch table list", "error");
      console.error(error);
      setLoading(false);
    }
  }, [toast]);

  const fetchDietaryOptions = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(DIETARY_OPTIONS_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          setDietaryOptions(response.data.data);
          setLoading(false);
        })
        .catch((error) => {
          toast("Failed to fetch dietary list", "error");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      toast("Failed to fetch dietary list", "error");
      console.error(error);
      setLoading(false);
    }
  }, [toast]);

  const fetchGuestOptions = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GET_GUEST_OPTIONS_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          setGuestOptions(response.data.data);
          setLoading(false);
        })
        .catch((error) => {
          toast("Failed to fetch guest options", "error");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      toast("Failed to fetch guest options", "error");
      console.error(error);
      setLoading(false);
    }
  }, [toast]);

  const backgroundRefresh = useCallback(() => {
    fetchGuestList(true);
  }, [fetchGuestList]);

  useEffect(() => {
    fetchGuestList();
    fetchGuestOptions();
    fetchEventDetails();
    fetchTableOptions();
    fetchDietaryOptions();
  }, [
    fetchGuestList,
    fetchGuestOptions,
    fetchEventDetails,
    fetchTableOptions,
    fetchDietaryOptions,
  ]);

  return (
    <LoadingInComponent isLoading={loading}>
      {/* Add Walk-In Guest Modal */}
      {showModal && (
        <CheckInForm
          formData={formData}
          toggleModal={toggleModal}
          handleCheckIn={validateCheckIn}
          handleFormInputChange={handleFormInputChange}
          handleDropDownChange={handleDropDownChange}
          handleSwitchChange={handleSwitchChange}
          dietaryOption={dietaryOptions}
          tableOption={tableOptions}
          guestOption={guestOptions}
          showReplacementSwitch={false}
        />
      )}

      {eventDetails && (
        <div
          className="flex flex-col lg:flex-row w-full h-screen"
          style={{
            color:
              eventDetails.customizations?.theme_color.toUpperCase() ?? "black",
          }}
        >
          <div className="w-full lg:w-[60%] h-screen">
            <div className="relative flex flex-col w-full h-screen">
              <ImageSlider imageList={eventDetails.image} />
              <Button
                variant="secondary"
                onClick={() => navigate(CHECKIN)}
                className="absolute top-4 left-4 shadow-lg z-20"
              >
                <XMarkIcon className="size-[16px]" />
                Exit Page
              </Button>
              <div className="lg:hidden absolute bottom-0 z-10 text-white flex flex-col w-full h-full py-8 px-6 justify-end text-center items-center gap-3 shadow-[inset_0px_-50px_80px_70px_rgba(0,_0,_0,_0.3)]">
                <h1
                  className="text-[48px]"
                  style={{
                    fontFamily:
                      eventDetails.customizations?.title_font?.family ??
                      "inherit",
                  }}
                >
                  {eventDetails.name}
                </h1>
                <p
                  className="text-[12px]"
                  style={{
                    fontFamily:
                      eventDetails.customizations?.content_font?.family ??
                      "inherit",
                  }}
                >
                  {eventDetails.description}
                </p>
              </div>
            </div>
          </div>

          {/* Right side content */}
          <div className="w-full lg:w-[550px] flex-1 lg:overflow-y-auto">
            <div
              className="flex flex-col w-full min-h-screen h-[1024px] px-8 lg:px-[96px] items-center justify-center gap-12"
              style={{
                backgroundColor: eventDetails.customizations?.background_image
                  ? "transparent"
                  : "white",
                color: eventDetails.customizations?.text_color,
                backgroundImage: eventDetails.customizations?.image
                  ? `url(${eventDetails.customizations?.image})`
                  : null,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="flex flex-col text-center gap-[32px]">
                <h2 className="text-lg">- Welcome to -</h2>
                <h1
                  className="text-[70px]"
                  style={{
                    fontFamily:
                      eventDetails.customizations?.title_font?.family ??
                      "inherit",
                  }}
                >
                  {eventDetails.name}
                </h1>
                <div className="flex flex-col w-full items-center text-center justify-center gap-4 lg:gap-2">
                  <div className="flex flex-col gap-1 lg:gap-0">
                    <h2 className="flex flex-col text-[24px] font-semibold">
                      <span className="lg:hidden">
                        {days[new Date(eventDetails.date).getDay()]}
                      </span>
                      <span></span>
                      {new Date(eventDetails.date).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </h2>
                  </div>
                  <div className="border-2 w-6" />
                  <p className="text-[12px] lg:text-[16px]">
                    {eventDetails.venue}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-4">
                <Button
                  variant="custom"
                  className="w-full border border-[#EFF0F2] text-[#C1C4CB] rounded-lg justify-start font-normal"
                  onClick={() => {
                    setCheckInOpen(!checkInOpen);
                  }}
                >
                  <SearchNormal1 size="16" />
                  Search
                </Button>
                <div className="grid grid-cols-2 gap-4">
                  <Button variant="secondary" onClick={toggleModal}>
                    Add Walk-In Guest
                  </Button>

                  <Button
                    variant="custom"
                    style={{
                      backgroundColor: normalizeColor(
                        eventDetails.customizations?.theme_color
                      ),
                      color: getContrastTextColor(
                        normalizeColor(eventDetails.customizations?.theme_color)
                      ),
                    }}
                    onClick={() => setQrCodeReaderOpen(!qrCodeReaderOpen)}
                  >
                    <QrCodeIcon className="size-[18px]" />
                    Scan Qr Code
                  </Button>
                </div>
              </div>
            </div>
            {qrCodeReaderOpen && (
              <div className="absolute top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50 backdrop-blur-lg">
                <div className="relative flex flex-col gap-4 w-fit h-fit p-6 bg-white rounded-lg">
                  <video
                    ref={videoRef}
                    style={{ width: "500px", borderRadius: "8px" }}
                    muted
                    autoPlay
                    playsInline
                  />

                  <div className="flex justify-end">
                    <Button
                      variant="secondary"
                      onClick={() => setQrCodeReaderOpen(false)}
                      className="w-fit"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {checkInOpen && (
              <CheckInSlider
                guestList={guestList}
                guestListRefresh={backgroundRefresh}
                setIsOpen={setCheckInOpen}
                guestUuid={guestId}
                inputRef={inputRef}
              />
            )}
          </div>
        </div>
      )}
    </LoadingInComponent>
  );
};

export default EventCheckInPage;
